/**
 * PdfPagesView — the uploaded PDF drawn page by page (pictures the server
 * renders), with the values of every line read from it marked where they are
 * written. Selecting a record in the grid scrolls here to it and lights its
 * values up; clicking a marked value opens its record in the grid.
 *
 * Where each line sits comes from the server (`ImportOriginal.locations`,
 * worked out from the PDF's own text — or, on a scan, only the page the AI
 * said it is on). Pages load as they scroll into view.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ZoomIn, ZoomOut } from 'lucide-react'
import type { ImportLineLocation, ImportOriginal } from './types'

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3]
/** CSS px per PDF point at 100%. */
const PX_PER_PT = 96 / 72
const MARK = { fill: 'rgba(250,204,21,0.38)', line: '#d97706' }

export interface PdfFocus {
  /** The selected record's lines. */
  lines: number[]
  /** Bumped on every selection that should scroll here. */
  seq: number
}

export function PdfPagesView({ original, fetchPage, focus, recOfLine, leftOut, onPickLine }: {
  original: ImportOriginal & { pages: Array<{ w: number; h: number }> }
  fetchPage: (page: number) => Promise<Blob>
  focus: PdfFocus | null
  recOfLine: Map<number, string>
  leftOut: Set<string>
  onPickLine: (line: number) => void
}) {
  // null = fit the pane's width.
  const [zoom, setZoom] = useState<number | null>(null)
  const [urls, setUrls] = useState<Record<number, string>>({})
  const [failed, setFailed] = useState<Record<number, string>>({})
  const [pulse, setPulse] = useState(0)
  const boxRef = useRef<HTMLDivElement>(null)
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const loading = useRef<Set<number>>(new Set())
  const made = useRef<string[]>([])
  const fetchRef = useRef(fetchPage)
  fetchRef.current = fetchPage

  const locations = original.locations || {}
  const byPage = useMemo(() => {
    const m = new Map<number, Array<{ line: number; loc: ImportLineLocation }>>()
    for (const [line, loc] of Object.entries(locations)) {
      if (!loc || !loc.page) continue
      const list = m.get(loc.page) || []
      list.push({ line: Number(line), loc })
      m.set(loc.page, list)
    }
    return m
  }, [locations])
  const selected = useMemo(() => new Set(focus?.lines || []), [focus])

  const load = useCallback((n: number) => {
    if (loading.current.has(n)) return
    loading.current.add(n)
    fetchRef.current(n)
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        made.current.push(url)
        setUrls((u) => ({ ...u, [n]: url }))
      })
      .catch((e) => {
        console.warn('[PdfPagesView] page could not be drawn', n, e)
        setFailed((f) => ({ ...f, [n]: 'This page could not be shown.' }))
      })
  }, [])

  // Draw pages as they come near the viewport.
  useEffect(() => {
    const root = boxRef.current
    if (!root) return undefined
    let io: IntersectionObserver | null = null
    try {
      io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue
          const n = Number((e.target as HTMLElement).dataset.page)
          if (n) load(n)
        }
      }, { root, rootMargin: '600px 0px' })
      pageRefs.current.forEach((el) => io!.observe(el))
    } catch (e) {
      // No observer (an old browser): draw them all.
      console.warn('[PdfPagesView] drawing every page up front', e)
      original.pages.forEach((_p, i) => load(i + 1))
    }
    return () => io?.disconnect()
  }, [original.pages, load])

  useEffect(() => () => {
    for (const u of made.current) {
      try { URL.revokeObjectURL(u) } catch { /* already gone */ }
    }
  }, [])

  // Scroll to the selected record: its first marked value a quarter of the
  // way down, or the top of its page when only the page is known.
  useEffect(() => {
    if (!focus || !focus.lines.length) return undefined
    const locs = focus.lines.map((l) => locations[String(l)]).filter(Boolean) as ImportLineLocation[]
    if (!locs.length) return undefined
    const first = locs.reduce((a, b) => {
      const ay = a.boxes[0]?.[1] ?? 0
      const by = b.boxes[0]?.[1] ?? 0
      return b.page < a.page || (b.page === a.page && by < ay) ? b : a
    })
    const box = boxRef.current
    const page = pageRefs.current.get(first.page)
    if (!box || !page) return undefined
    // After layout, so a just-mounted view scrolls too.
    const t = window.setTimeout(() => {
      try {
        const y = first.boxes.length ? Math.min(...first.boxes.map((b) => b[1])) : 0
        const top = page.offsetTop + y * page.clientHeight
          - (first.boxes.length ? box.clientHeight / 4 : 8)
        // Sideways only when the value is out of view (zoomed in).
        const x = first.boxes.length ? page.offsetLeft + first.boxes[0][0] * page.clientWidth : null
        const left = x != null && (x < box.scrollLeft || x > box.scrollLeft + box.clientWidth - 40)
          ? x - 24 : box.scrollLeft
        box.scrollTo({ top: Math.max(0, top), left: Math.max(0, left), behavior: 'smooth' })
        setPulse((p) => p + 1)
      } catch (e) {
        console.warn('[PdfPagesView] could not scroll to the record', e)
      }
    }, 30)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.seq])

  const i = zoom == null ? -1 : ZOOMS.indexOf(zoom)
  const step = (d: number) => {
    const cur = zoom == null ? 2 : i
    setZoom(ZOOMS[Math.max(0, Math.min(ZOOMS.length - 1, cur + d))])
  }
  const located = Object.keys(locations).length

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <style>{`@keyframes twPdfPulse{0%{box-shadow:0 0 0 0 rgba(217,119,6,.7)}100%{box-shadow:0 0 0 10px rgba(217,119,6,0)}}`}</style>
      <div style={toolbar}>
        <button onClick={() => step(-1)} style={tool} aria-label="Zoom out" title="Zoom out"><ZoomOut size={15} /></button>
        <button onClick={() => step(1)} style={tool} aria-label="Zoom in" title="Zoom in"><ZoomIn size={15} /></button>
        <button onClick={() => setZoom(null)} aria-pressed={zoom == null} style={seg(zoom == null)}>Fit width</button>
        <button onClick={() => setZoom(1)} aria-pressed={zoom === 1} style={seg(zoom === 1)}>100%</button>
        <span style={{ marginLeft: 'auto', color: 'var(--color-text-secondary, #5e6472)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          title={located ? 'Marked values open their record in the grid' : 'The values could not be found on the pages (a scan has no text to search)'}>
          {original.pages.length} page{original.pages.length === 1 ? '' : 's'}
          {zoom == null ? ' · fit' : ` · ${Math.round(zoom * 100)}%`}
        </span>
      </div>
      {/* Positioned, so each page's offsetTop is measured from here. */}
      <div ref={boxRef} style={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'auto', background: 'var(--color-bg-secondary, #e9e8e3)', padding: 12 }}>
        {original.pages.map((p, idx) => {
          const n = idx + 1
          const marks = byPage.get(n) || []
          const pageOnly = marks.some((m) => selected.has(m.line) && !m.loc.boxes.length)
          return (
            <div
              key={n} data-page={n}
              ref={(el) => { if (el) pageRefs.current.set(n, el); else pageRefs.current.delete(n) }}
              aria-label={`Page ${n}`}
              style={{
                position: 'relative', margin: '0 auto 12px', background: '#fff', maxWidth: zoom == null ? '100%' : 'none',
                width: zoom == null ? '100%' : `${Math.round(p.w * zoom * PX_PER_PT)}px`,
                aspectRatio: `${p.w} / ${p.h}`,
                boxShadow: pageOnly ? `0 0 0 3px ${MARK.line}` : '0 1px 4px rgba(0,0,0,0.18)',
              }}
            >
              {urls[n]
                ? <img src={urls[n]} alt={`Page ${n} of ${original.name}`} draggable={false}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', userSelect: 'none' }} />
                : <span style={{ position: 'absolute', top: 12, left: 12, fontSize: 13, color: failed[n] ? '#b42318' : 'var(--color-text-secondary, #6b7180)' }}>
                    {failed[n] || `Loading page ${n}…`}
                  </span>}
              {marks.flatMap(({ line, loc }) => {
                const id = recOfLine.get(line)
                const gone = !!id && leftOut.has(id)
                const on = selected.has(line)
                return loc.boxes.map((b, k) => (
                  <button
                    key={`${line}:${k}`}
                    type="button"
                    onClick={() => onPickLine(line)}
                    disabled={gone}
                    aria-label={`Open the record of line ${line}`}
                    title={gone ? 'Left out of this import' : 'Open this record in the grid'}
                    className="tw-pdf-mark"
                    style={{
                      position: 'absolute', padding: 0, cursor: gone ? 'default' : 'pointer',
                      left: `${b[0] * 100}%`, top: `${b[1] * 100}%`,
                      width: `${b[2] * 100}%`, height: `${b[3] * 100}%`,
                      // A little room around the text, so a mark reads as a highlight.
                      margin: '-2px -3px', boxSizing: 'content-box', paddingRight: 6, paddingBottom: 4,
                      borderRadius: 3,
                      border: on ? `1.5px solid ${MARK.line}` : '1px solid transparent',
                      background: on ? MARK.fill : 'transparent',
                      animation: on && k === 0 && pulse ? 'twPdfPulse 0.9s ease-out 2' : undefined,
                    }}
                  />
                ))
              })}
              {pageOnly && (
                <span style={{ position: 'absolute', top: 8, right: 8, fontSize: 13, fontWeight: 600, padding: '2px 8px', borderRadius: 6, background: MARK.line, color: '#fff' }}>
                  The selected record is on this page
                </span>
              )}
            </div>
          )
        })}
      </div>
      <style>{`.tw-pdf-mark:not(:disabled):hover{border-color:#2f5fe3 !important;background:rgba(47,95,227,0.10)}`}</style>
    </div>
  )
}

const toolbar: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', fontSize: 13,
  borderBottom: '1px solid var(--color-border, #e6e5e0)',
}
const tool: React.CSSProperties = {
  width: 30, height: 30, flexShrink: 0, borderRadius: 8, border: '1px solid var(--color-border, #e6e5e0)',
  background: 'var(--color-bg, #fff)', color: 'var(--color-text-secondary, #4a4f5a)', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
}
function seg(on: boolean): React.CSSProperties {
  return {
    height: 24, padding: '0 9px', borderRadius: 5, border: 0, cursor: 'pointer', fontSize: 13, fontWeight: 500,
    background: on ? 'var(--color-text, #15171c)' : 'transparent',
    color: on ? 'var(--color-bg, #fff)' : 'var(--color-text-secondary, #4a4f5a)',
  }
}
